/*
 * "Only N in stock" over a + that has reached the stock limit.
 *
 * A + at the limit is not `disabled`: a disabled button gets no hover and no
 * click, so the customer would not learn why nothing happens. It carries
 * aria-disabled="true" (looks disabled, see the CSS of each stepper) and
 * data-stock-limit="N" (empty when the number is not known); a warranty row
 * capped by its printer's quantity has data-limit-reason="warranty". Hovering it with
 * a mouse shows the hint while the pointer is on it; a click or tap shows it
 * for a moment. The click itself goes no further: nothing is added.
 *
 * The hint is a popover (top layer): the cart drawer is a modal <dialog>,
 * which sits above anything else on the page, so a plain fixed element
 * would be hidden behind it.
 *
 * Set by assets/card-cart-stepper.js (product cards, product page, floating
 * bar) and assets/cart-line-motion.js (cart drawer and cart page rows).
 */

const AT_LIMIT = '[aria-disabled="true"][data-stock-limit]';
const SHOW_AFTER_TAP_MS = 2500;

const strings = (() => {
  try {
    return JSON.parse(document.getElementById('card-cart-stepper-strings')?.textContent ?? '{}');
  } catch {
    return {};
  }
})();

/** @type {HTMLElement | null} */
let hint = null;
let hideTimer = 0;

function messageFor(button) {
  if (button.dataset.limitReason === 'warranty' && strings.warrantyLimit) return strings.warrantyLimit;
  const limit = Number.parseInt(button.dataset.stockLimit ?? '', 10);
  if (Number.isFinite(limit) && limit > 0 && strings.stockLimit) {
    return strings.stockLimit.replace('[quantity]', String(limit));
  }
  return strings.stockLimitUnknown ?? 'No more in stock';
}

function show(button) {
  clearTimeout(hideTimer);
  if (!hint) {
    hint = document.createElement('div');
    hint.className = 'stock-limit-hint';
    hint.setAttribute('role', 'status');
    hint.popover = 'manual';
    document.body.append(hint);
  }
  hint.textContent = messageFor(button);
  // Shown again each time, so it lands above a dialog opened since.
  if (hint.matches(':popover-open')) hint.hidePopover();
  hint.showPopover();

  // Above the button, centred on it, kept inside the viewport.
  const box = button.getBoundingClientRect();
  const { width, height } = hint.getBoundingClientRect();
  const left = Math.min(Math.max(8, box.left + box.width / 2 - width / 2), window.innerWidth - width - 8);
  const top = box.top - height - 8 < 8 ? box.bottom + 8 : box.top - height - 8;
  hint.style.left = `${left}px`;
  hint.style.top = `${top}px`;
}

function hide() {
  clearTimeout(hideTimer);
  if (hint?.matches(':popover-open')) hint.hidePopover();
}

const atLimit = (target) => (target instanceof Element ? target.closest(AT_LIMIT) : null);

// Window, capture phase: before the steppers' own listeners on document and
// the theme's on:click on the button.
window.addEventListener(
  'click',
  (event) => {
    const button = atLimit(event.target);
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    show(/** @type {HTMLElement} */ (button));
    hideTimer = window.setTimeout(hide, SHOW_AFTER_TAP_MS);
  },
  true
);

window.addEventListener(
  'pointerover',
  (event) => {
    if (event.pointerType !== 'mouse') return;
    const button = atLimit(event.target);
    if (button) show(/** @type {HTMLElement} */ (button));
  },
  true
);

window.addEventListener(
  'pointerout',
  (event) => {
    if (event.pointerType !== 'mouse') return;
    const button = atLimit(event.target);
    if (button && !button.contains(/** @type {Node | null} */ (event.relatedTarget))) hide();
  },
  true
);

// The hint is fixed to where the button was: it goes away on scroll.
window.addEventListener('scroll', hide, { passive: true, capture: true });
